import React, { useState } from 'react';
import { CircularProgress } from '@mui/material';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Checkbox } from '../../components/UI/Checkbox/Checkbox';
import { useApi } from '../../hooks/useApi';
import { useStores } from '../../hooks/useStore';
import {
  AudioBlockData,
  ButtonBlockData,
  Course,
  CourseModule,
  HeadingBlockData,
  ImageBlockData,
  Lesson,
  LessonBlock,
  LessonBlockData,
  ParagraphBlockData,
  VideoBlockData,
} from '../../models/courses';

import { SortableBlock } from './components/blocks/SortableBlock';
import { BLOCK_META, BLOCK_ORDER, BlockType } from './components/blocks/blockMeta';
import { VideoBlock } from './components/blocks/VideoBlock';
import { ParagraphBlock } from './components/blocks/ParagraphBlock';
import { ImageBlock } from './components/blocks/ImageBlock';
import { AudioBlock } from './components/blocks/AudioBlock';
import { HeadingBlock } from './components/blocks/HeadingBlock';
import { ButtonBlock } from './components/blocks/ButtonBlock';
import styles from './LessonEditor.module.css';

interface LessonEditorProps {
  course: Course;
  module: CourseModule;
  lesson: Lesson;
  onBack: () => void;
  onSaved: () => void;
  onBackToCourses: () => void;
  onBackToModules: () => void;
}

function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2);
}

function createEmptyBlock(type: BlockType): LessonBlock {
  const id = generateId();
  switch (type) {
    case 'video':
      return {
        id,
        type,
        data: { provider: 'kinescope', video_id: '', caption: '' },
      };
    case 'paragraph':
      return { id, type, data: { text: '' } };
    case 'image':
      return { id, type, data: { src: '', alt: '', caption: '' } };
    case 'audio':
      return { id, type, data: { src: '', caption: '' } };
    case 'heading':
      return { id, type, data: { level: 2, text: '' } };
    case 'button':
      return { id, type, data: { text: '', url: '' } };
    default: {
      const _exhaustive: never = type;
      throw new Error(`Unknown block type: ${_exhaustive}`);
    }
  }
}

const LessonEditor: React.FC<LessonEditorProps> = ({
  course,
  module,
  lesson,
  onBack,
  onSaved,
  onBackToCourses,
  onBackToModules,
}) => {
  const api = useApi();
  const store = useStores();

  const [title, setTitle] = useState<string>(lesson.title);
  const [durationMinutes, setDurationMinutes] = useState<number>(
    lesson.duration_minutes ?? 0,
  );
  const [isPreview, setIsPreview] = useState<boolean>(
    lesson.is_preview ?? false,
  );
  const [blocks, setBlocks] = useState<LessonBlock[]>(
    lesson.content?.blocks ?? [],
  );
  const [saving, setSaving] = useState<boolean>(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const handleDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseInt(e.target.value, 10);
    setDurationMinutes(isNaN(val) ? 0 : val);
  };

  const handleAddBlock = (type: BlockType) => {
    setBlocks((prev) => [...prev, createEmptyBlock(type)]);
  };

  const handleUpdateBlock = (index: number, data: LessonBlockData) => {
    setBlocks((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], data };
      return updated;
    });
  };

  const handleDeleteBlock = (id: string) => {
    setBlocks((prev) => prev.filter((b) => b.id !== id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }
    setBlocks((prev) => {
      const oldIndex = prev.findIndex((b) => b.id === active.id);
      const newIndex = prev.findIndex((b) => b.id === over.id);
      if (oldIndex === -1 || newIndex === -1) {
        return prev;
      }
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  // Блоки урока хранятся как произвольный JSON и не резолвятся бэкендом при
  // чтении, поэтому в data.src кладём готовый абсолютный url, а не key.
  const handleUploadImage = async (file: File): Promise<string> => {
    const { url } = await api.upload.image(file);
    return url;
  };

  const handleUploadAudio = async (file: File): Promise<string> => {
    const { url } = await api.upload.audio(file);
    return url;
  };

  const handleSave = async () => {
    if (!title.trim()) {
      store.notificationsStore.setError('Название урока обязательно');
      return;
    }

    try {
      setSaving(true);
      await api.courses.updateLesson(
        {
          title,
          duration_minutes: durationMinutes,
          is_preview: isPreview,
          content: { version: 1, blocks },
        },
        lesson.id,
      );
      onSaved();
    } catch {
      store.notificationsStore.setError('Ошибка при сохранении урока');
    } finally {
      setSaving(false);
    }
  };

  const renderBlockEditor = (block: LessonBlock, index: number) => {
    switch (block.type) {
      case 'video':
        return (
          <VideoBlock
            data={block.data as VideoBlockData}
            onChange={(d) => handleUpdateBlock(index, d)}
            disabled={saving}
          />
        );
      case 'paragraph':
        return (
          <ParagraphBlock
            data={block.data as ParagraphBlockData}
            onChange={(d) => handleUpdateBlock(index, d)}
            disabled={saving}
          />
        );
      case 'image':
        return (
          <ImageBlock
            data={block.data as ImageBlockData}
            onChange={(d) => handleUpdateBlock(index, d)}
            onUpload={handleUploadImage}
            disabled={saving}
          />
        );
      case 'audio':
        return (
          <AudioBlock
            data={block.data as AudioBlockData}
            onChange={(d) => handleUpdateBlock(index, d)}
            onUpload={handleUploadAudio}
            disabled={saving}
          />
        );
      case 'heading':
        return (
          <HeadingBlock
            data={block.data as HeadingBlockData}
            onChange={(d) => handleUpdateBlock(index, d)}
            disabled={saving}
          />
        );
      case 'button':
        return (
          <ButtonBlock
            data={block.data as ButtonBlockData}
            onChange={(d) => handleUpdateBlock(index, d)}
            disabled={saving}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className={styles.editor}>
      <div className={styles.breadcrumb}>
        <span className={styles.crumb} onClick={onBackToCourses}>
          Курсы
        </span>
        <span className={styles.crumbSep}>›</span>
        <span className={styles.crumb} onClick={onBackToModules}>
          {course.title}
        </span>
        <span className={styles.crumbSep}>›</span>
        <span className={styles.crumb} onClick={onBack}>
          {module.title}
        </span>
        <span className={styles.crumbSep}>›</span>
        <span className={styles.crumbCurrent}>{lesson.title}</span>
      </div>

      {/* Мета-данные урока */}
      <div className={styles.meta}>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Название урока</span>
          <input
            className={styles.titleInput}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Введите название урока"
            disabled={saving}
          />
        </div>
        <div className={styles.metaRow}>
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Длительность</span>
            <div className={styles.durationWrap}>
              <input
                className={styles.durationInput}
                type="number"
                min={0}
                value={durationMinutes}
                onChange={handleDurationChange}
                disabled={saving}
              />
              <span>мин</span>
            </div>
          </div>
          <div className={styles.toggleWrap}>
            <Checkbox
              label="Превью (доступен без покупки)"
              checked={isPreview}
              onChange={(e) => setIsPreview(e.target.checked)}
              disabled={saving}
            />
          </div>
        </div>
      </div>

      {/* Блоки контента */}
      <div className={styles.sectionLabel}>Контент урока</div>

      <div className={styles.toolbar}>
        {BLOCK_ORDER.map((type) => {
          const meta = BLOCK_META[type];
          const Icon = meta.icon;
          return (
            <button
              key={type}
              type="button"
              className={styles.toolbarBtn}
              onClick={() => handleAddBlock(type)}
              disabled={saving}
            >
              <Icon />
              {meta.label}
            </button>
          );
        })}
      </div>

      {blocks.length === 0 ? (
        <div className={styles.empty}>
          <span className={styles.emptyTitle}>Пока пусто</span>
          <span className={styles.emptyHint}>
            Добавьте первый блок из панели выше — он появится здесь.
          </span>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={blocks.map((b) => b.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className={styles.blocks}>
              {blocks.map((block, index) => (
                <SortableBlock
                  key={block.id}
                  id={block.id}
                  type={block.type}
                  onDelete={() => handleDeleteBlock(block.id)}
                  disabled={saving}
                >
                  {renderBlockEditor(block, index)}
                </SortableBlock>
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Sticky-панель действий */}
      <div className={styles.actionBar}>
        <button
          type="button"
          className={styles.cancelBtn}
          onClick={onBack}
          disabled={saving}
        >
          Отмена
        </button>
        <button
          type="button"
          className={styles.saveBtn}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            'Сохранить'
          )}
        </button>
      </div>
    </div>
  );
};

export default LessonEditor;
